import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as PageChatCtx from './chat-ctx';
import { ChatMsgArea } from './chat-msg-area/chat-msg-area';
import { ChatUserList } from './chat-user-list/chat-user-list';
import { SearchInput } from '../../common/search-input';
import { LoadingDiscoWithContainer } from '../../common/loading/loading-disco';
import { useBusiness, useUser } from '../../../graphql/graph-hooks';
import { GET_CHAT_PAGE_LISTS_PAGINATED } from '../../../graphql/queries/conversation-page-lists';
import { GET_ADMIN_CONVERSATIONS } from '../../../graphql/queries/admin-conversations';
import { logError } from '../../../helpers/errors/bug-report';
import { useDebounce } from 'helpers/hooks/use-debounce';
import { useModal, useFetchMore as useFetchMoreConversations } from 'components/global-hooks';
import { UI_MODALS } from 'helpers/enums';

import S from './chat-page.module.scss';
import { CONVERSATION_MARK_AS_READ } from 'graphql/mutations/conversation-mark-as-read';
import { NetworkStatus } from 'apollo-boost';
import { sortConversations } from 'helpers/sorts';
import { useGlobalCtx } from 'components/global-context';

const ChatPage = ({ isAdminChat, chatBizId }) => {
  const { initModal } = useModal();
  const { t, i18n } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const { state } = useGlobalCtx();
  const unreadBusinessCount = isAdminChat ? state.app.unreadAdmin : state.app.unreadBusiness;

  const {
    dispatch,
    state: { filter, conversationId },
  } = PageChatCtx.usePageChatCtx();

  const { id: userId, admin: isAdmin } = useUser();
  const { id: businessId } = useBusiness();

  const handleFilterChange = (text) => dispatch(PageChatCtx.setChatFields({ filter: text, conversationId: null }));
  const { _value, onChange: onFilterChange } = useDebounce(filter, handleFilterChange);

  const conversationsQuery = isAdminChat ? GET_ADMIN_CONVERSATIONS : GET_CHAT_PAGE_LISTS_PAGINATED;

  const variables = isAdminChat
    ? {
        first: 20,
        senderSearch: filter,
        sortByUnreadMessages: true,
        sortByLastMessageSent: true,
      }
    : {
        first: 20,
        senderSearch: filter,
        sortByUnreadMessages: true,
        sortByLastMessageSent: true,
        blockedByBusinessId: businessId,
        inGroupBusinessId: businessId,
        chatBizId,
        userId,
        isAdmin,
      };

  const { loading, error, data, refetch, fetchMore, networkStatus } = useQuery(conversationsQuery, {
    variables: variables,
    fetchPolicy: 'cache-and-network',
    onError: (err) => logError(err, 'GET_CHAT_PAGE_LISTS_PAGINATED', ChatPage.displayName),
  });

  const {
    isLoading: isFetchMoreLoading,
    fetchMore: fetchMoreConversations,
    error: fetchMoreError,
  } = useFetchMoreConversations(isAdminChat ? 'admin_conversations' : 'conversations_paginated');

  const [markAsRead] = useMutation(CONVERSATION_MARK_AS_READ, {
    variables: {
      id: conversationId,
      isAdmin,
      blockedByBusinessId: businessId,
      inGroupBusinessId: businessId,
    },
  });

  const handleAvatarImageClick = (dancerId) => {
    initModal(UI_MODALS.DANCER, {
      dancerId,
    });
  };

  const handleOnScrollBottom = () => {
    const pageInfo = isAdminChat ? data?.admin_conversations?.pageInfo : data?.conversations_paginated?.pageInfo;
    if (loading || isFetchMoreLoading || !pageInfo?.hasNextPage) {
      return;
    }
    fetchMoreConversations(fetchMore, {
      after: pageInfo?.endCursor,
      sortByLastMessageSent: true,
      sortByUnreadMessages: true,
    });
  };

  useEffect(() => {
    if (!data) return;
    setConversations(isAdminChat ? data?.admin_conversations?.nodes : data?.conversations_paginated?.nodes);
  }, [data, isAdminChat]);

  useEffect(() => {
    if (conversationId && conversations) {
      const conversation = conversations.find(({ id }) => id === conversationId);
      if (conversation?.business_unread_messages_count && (!isAdmin || isAdminChat)) {
        markAsRead();
      }
    }
  }, [conversationId, conversations, isAdminChat, isAdmin, markAsRead, businessId]);

  useEffect(() => {
    const cid = new URLSearchParams(window.location.search).get('cid');
    if (!conversationId && cid) {
      dispatch(PageChatCtx.setChatFields({ conversationId: cid }));
    }
  }, [conversationId, dispatch]);

  useEffect(() => {
    return () => {
      // Reset conversationId when component unmounts
      dispatch(PageChatCtx.setChatFields({ conversationId: null }));
    };
  }, [isAdminChat, dispatch]);

  useEffect(() => {
    if (unreadBusinessCount) {
      refetch();
    }
  }, [unreadBusinessCount, refetch]);

  const sortedConversations = useMemo(() => {
    return sortConversations(conversations || []);
  }, [conversations]);

  if (error) {
    return <h1 className={S.errorPage}>{t('chatPage.error_loading_conversations')}</h1>;
  }

  return (
    <div className={S.pageWrapper}>
      <h1 className={S.h1Heading}>
        {isAdminChat ? 'Admin ' : ''}
        {i18n.language === 'en' ? t('chatPage.conversations') : t('chatPage.conversations').toLowerCase()}
      </h1>
      <div className={S.chatArea}>
        <div className={S.chatListArea}>
          <div className={S.space} />
          <SearchInput
            className={S.searchInput}
            placeholder={t('chatPage.search_conversations')}
            onChange={onFilterChange}
          />
          {loading && !conversations?.length ? (
            <LoadingDiscoWithContainer />
          ) : (
            <ChatUserList
              isLoading={loading || isFetchMoreLoading}
              conversations={sortedConversations}
              onScrollBottom={handleOnScrollBottom}
              avatarOnClick={handleAvatarImageClick}
              error={error || fetchMoreError}
              isAdminChat={isAdminChat}
              isAdmin={isAdmin}
            />
          )}
        </div>
        <ChatMsgArea
          isAdminChat={isAdminChat}
          avatarOnClick={handleAvatarImageClick}
          isLoading={networkStatus === NetworkStatus.loading}
          conversations={sortedConversations}
          setConversations={setConversations}
        />
      </div>
    </div>
  );
};

ChatPage.displayName = 'ChatPage';
ChatPage.propTypes = {
  isAdminChat: PropTypes.bool.isRequired,
  chatBizId: PropTypes.string,
};

export { ChatPage };
